import styled from "styled-components";
import { useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import { CountdownCircleTimer } from "react-countdown-circle-timer";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";

import Tip from "components/other/Tip";
import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";

import challengeIcon from "assets/img/icons/challenge-icon.svg";
import reloadIcon from "assets/img/icons/reload-icon.svg";
import reloadWhiteIcon from "assets/img/icons/reload-icon-white.svg";
import {
  capitalizeFirstLetter,
  getRandomNumberInRange,
} from "modules/functions";

const ChallengeBox = styled.div`
  width: 310px;
  padding: ${css.BOX_PADDING_SECONDARY};
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const TitleLine = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TitleLineContent = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const TitleIcon = styled.div`
  width: 26px;
  height: 26px;
  min-height: 26px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 500;
`;

const ReloadBox = styled.div`
  opacity: ${(props) => (props.submitted ? 0 : 1)};

  transition: all 0.2s;
`;

const TaskWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const TaskBox = styled.div`
  padding: 11px 20px;
  box-sizing: border-box;

  background: ${css.BRAND_BLUSH};
  border-radius: 10px;

  display: flex;
  align-items: center;
  gap: 9px;
`;

const TaskText = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: #000;
`;

const Task = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const TaskIconBig = styled.div`
  width: 30px;
  height: 30px;
  min-height: 30px;
`;

const TaskIconMiddle = styled.div`
  width: 24px;
  height: 24px;
  min-height: 24px;
`;

const TaskIconSmall = styled.div`
  width: 15px;
  height: 15px;
  min-height: 15px;
`;

const SelectionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ResultButtonFixed = styled.div`
  box-sizing: border-box;
  width: 40px;
  height: 28px;
  padding: 0;

  background: ${(props) =>
    props.clicked ? css.BRAND_GREEN : css.BRAND_YELLOW};
  border-radius: 14px;
  border: 0;

  font-family: "Roboto", sans-serif;
  font-weight: 400;
  font-size: 13px;
  color: ${(props) => (props.clicked ? "#fff" : css.DARK_BLUE)};
  white-space: nowrap;

  transition: 0.2s;

  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) =>
    !props.submitted &&
    `&:hover {
    cursor: pointer;

    color: #fff;

    background: ${css.BRAND_GREEN};
  }`}
`;

const ResultButton = styled.div`
  box-sizing: border-box;
  height: 28px;
  padding: 0 9px;

  background: ${(props) =>
    props.clicked ? css.BRAND_GREEN : css.BRAND_YELLOW};
  border-radius: 14px;
  border: 0;

  font-family: "Roboto", sans-serif;
  font-weight: 400;
  font-size: 13px;
  color: ${(props) => (props.clicked ? "#fff" : css.DARK_BLUE)};
  white-space: nowrap;

  transition: 0.2s;

  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) =>
    !props.submitted &&
    `&:hover {
    cursor: pointer;

    color: #fff;

    background: ${css.BRAND_GREEN};
  }`}
`;

const WrongWrap = styled.div`
  min-height: 98px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const WrongText = styled.div``;

const CountdownText = styled.div`
  font-size: 22px;

  transform: translateY(-1px);
`;

const ColorOption = styled.div`
  width: 18px;
  height: 18px;
  min-height: 18px;

  border-radius: 9px;
  background-color: ${(props) => props.color};
`;

export default ({
  data,
  changeChallenge,
  onSubmit,
  optionClicked,
  showWrong,
  tryNewChallenge,
}) => {
  const taskType = data.challengeData.type;
  const { taskData } = data.challengeData;

  let taskText;
  let taskIcons = [];
  let optionButtons = [];

  if (taskType === "calculation") {
    taskText = "Calculate";

    taskIcons.push(
      <TaskIconBig>
        <img
          src={`/assets-public/img/challenge-icons/${taskData.icon1}`}
          style={{ width: "100%" }}
        />
      </TaskIconBig>
    );

    taskIcons.push(
      <TaskIconSmall>
        <img
          src={`/assets-public/img/challenge-icons/${taskData.icon2}`}
          style={{ width: "100%" }}
        />
      </TaskIconSmall>
    );

    taskIcons.push(
      <TaskIconBig>
        <img
          src={`/assets-public/img/challenge-icons/${taskData.icon3}`}
          style={{ width: "100%" }}
        />
      </TaskIconBig>
    );

    for (const option of taskData.options) {
      optionButtons.push(
        <ResultButtonFixed
          key={option}
          onClick={() => onSubmit(option)}
          clicked={option === optionClicked}
          submitted={optionClicked ? 1 : 0}
        >
          {option}
        </ResultButtonFixed>
      );
    }
  }

  if (taskType === "colors") {
    taskText = "Pick";

    for (const icon of taskData.icons) {
      taskIcons.push(
        <TaskIconMiddle>
          <img
            src={`/assets-public/img/challenge-icons/${icon}`}
            style={{ width: "100%" }}
          />
        </TaskIconMiddle>
      );
    }

    for (const option of taskData.options) {
      optionButtons.push(
        <ResultButtonFixed
          key={option}
          onClick={() => onSubmit(option)}
          clicked={option === optionClicked}
          submitted={optionClicked ? 1 : 0}
        >
          <ColorOption color={option} />
        </ResultButtonFixed>
      );
    }
  }

  if (taskType === "picture") {
    taskText = "Select";

    taskIcons.push(
      <TaskIconBig>
        <img
          src={`/assets-public/img/challenge-icons/${taskData.picture}`}
          style={{ width: "100%" }}
        />
      </TaskIconBig>
    );

    for (const option of taskData.options) {
      optionButtons.push(
        <ResultButton
          key={option}
          onClick={() => onSubmit(option)}
          clicked={option === optionClicked}
          submitted={optionClicked ? 1 : 0}
        >
          {capitalizeFirstLetter(option)}
        </ResultButton>
      );
    }
  }

  const renderTime = ({ remainingTime }) => {
    return <CountdownText>{remainingTime}</CountdownText>;
  };

  return (
    <ChallengeBox>
      <TitleLine>
        <TitleLineContent>
          <TitleIcon>
            <img src={challengeIcon} style={{ width: "100%" }} />
          </TitleIcon>
          <Title>Challenge</Title>
          <Tip
            tipId="challenge"
            tip="A challenge is a simple task that helps us distinguish real human users and detect prohibited practices, thereby increasing value for our advertisers.<br/><br/>Complete the required task and continue browsing posts."
            position="bottom"
          />
        </TitleLineContent>
        <ReloadBox submitted={optionClicked ? 1 : 0}>
          <ButtonRoundIcon
            icon={reloadIcon}
            hoverIcon={reloadWhiteIcon}
            background={css.BRAND_YELLOW}
            hoverBackground={css.BRAND_GREEN}
            onClick={changeChallenge}
          />
        </ReloadBox>
      </TitleLine>
      {!showWrong && (
        <TaskWrap>
          <TaskBox>
            <TaskText>{taskText}:</TaskText>
            <Task>{taskIcons}</Task>
          </TaskBox>
          <SelectionButtons>{optionButtons}</SelectionButtons>
        </TaskWrap>
      )}
      {showWrong && (
        <WrongWrap>
          <WrongText>Wrong answer, please try again.</WrongText>
          <CountdownCircleTimer
            isPlaying
            duration={10}
            colors={["#ead015", "#5dd39e"]}
            colorsTime={[7, 0]}
            size={60}
            strokeWidth={6}
            trailColor="rgb(255, 255, 255, 0.07)"
            onComplete={tryNewChallenge}
          >
            {renderTime}
          </CountdownCircleTimer>
        </WrongWrap>
      )}
    </ChallengeBox>
  );
};
